import React, { useMemo, useState } from "react";
import {
  Box, Tabs, Tab, Typography, IconButton, Chip, alpha, Paper, Grid, Button,
  Divider, CircularProgress,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import BusinessIcon from "@mui/icons-material/Business";
import GroupIcon from "@mui/icons-material/Group";
import HistoryIcon from "@mui/icons-material/History";
import PaidIcon from "@mui/icons-material/Paid";
import DevicesIcon from "@mui/icons-material/Devices";
import PlaylistPlayIcon from "@mui/icons-material/PlaylistPlay";
import PermMediaIcon from "@mui/icons-material/PermMedia";
import CollectionsBookmarkIcon from "@mui/icons-material/CollectionsBookmark";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { useSelector } from "react-redux";
import {
  useGetCompanyQuery,
  useGetDevicesQuery,
  useUpdateCompanyPlanMutation,
  useExtendTrialMutation,
  useSuspendCompanyMutation,
  useReactivateCompanyMutation,
  useGenerateInstallerPinMutation,
} from "../../../Api/attoDeskApi";
import { useNotifier } from "../../../Core/Notifier";
import {
  Dialog, DialogActions, DialogContent, DialogTitle, TextField, MenuItem,
} from "@mui/material";
import { selectIsSuperAdmin } from "../../../Store/Auth/AuthSelector";
import SignageUsersList from "../../SignageUsers/List";
import SignageActivityList from "../../SignageActivity/List";
import CompanyEdit from "../Edit";
import PageLoader from "../../../Components/Loader/PageLoader";
import {
  NAVY, GOLD, GOLD_SOFT, BORDER, TEXT_PRIMARY, TEXT_SECONDARY,
} from "../../../theme/tokens";

const formatMoney = (n: number): string =>
  new Intl.NumberFormat(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);

const TabPanel: React.FC<{ value: number; index: number; children: React.ReactNode }> = ({
  value, index, children,
}) => value === index ? <Box sx={{ pt: 3 }}>{children}</Box> : null;

const CompanyDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const companyId = Number(id);
  const navigate = useNavigate();
  const isSuper = useSelector(selectIsSuperAdmin);
  const [tab, setTab] = useState(0);

  const { data, isLoading } = useGetCompanyQuery({ searchText: "", id: String(companyId) });
  const company = useMemo(() => {
    const arr = Array.isArray(data?.data) ? (data?.data as any[]) : [];
    return arr.find((c) => c.id === companyId) || null;
  }, [data, companyId]);

  // Pre-fetched device list for the Billing tab summary.
  const { data: devicesRes } = useGetDevicesQuery({ company_id: companyId }, {
    skip: !companyId,
  });
  const devices: any[] = Array.isArray(devicesRes?.data) ? (devicesRes?.data as any[]) : [];

  if (isLoading) return <PageLoader height="20vh" />;

  if (!company) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Typography sx={{ fontSize: 14, color: TEXT_SECONDARY }}>
          Company not found, or you don't have access to view it.
        </Typography>
        <Button onClick={() => navigate("/company/list")} sx={{ mt: 2, textTransform: "none" }}>
          Back to companies
        </Button>
      </Box>
    );
  }

  const activeDevices = devices.filter((d: any) => !d.is_disabled);
  const monthlyBilling = devices.reduce(
    (s: number, d: any) => s + (typeof d.monthly_rate === "number" ? d.monthly_rate : 0),
    0
  );

  return (
    <Box sx={{ bgcolor: "#f1f5f9", minHeight: "calc(100vh - 100px)", mx: -3, mt: -3 }}>
      {/* Header */}
      <Box sx={{
        background: "linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%)",
        px: 3, position: "sticky", top: 0, zIndex: 10,
        boxShadow: "0 4px 30px rgba(0,0,0,0.18)",
      }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, py: 2 }}>
          <IconButton onClick={() => navigate("/company/list")} size="small"
            sx={{
              border: "1px solid rgba(255,255,255,0.15)", borderRadius: 2,
              width: 38, height: 38, bgcolor: "rgba(255,255,255,0.05)",
              "&:hover": { bgcolor: "rgba(255,255,255,0.12)" },
            }}>
            <ArrowBackIcon sx={{ fontSize: 18, color: "#e2e8f0" }} />
          </IconButton>
          <Divider orientation="vertical" flexItem sx={{ borderColor: "rgba(255,255,255,0.1)" }} />
          <Box sx={{
            width: 40, height: 40, borderRadius: 1.5,
            display: "flex", alignItems: "center", justifyContent: "center",
            background: "linear-gradient(135deg, #7c3aed 0%, #8b5cf6 100%)",
            boxShadow: "0 4px 12px rgba(124,58,237,0.45)",
          }}>
            <BusinessIcon sx={{ fontSize: 20, color: "#fff" }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
              <Typography fontSize={20} fontWeight={800} color="#fff" letterSpacing="-0.02em">
                {company.companyName}
              </Typography>
              {company.subscription_plan && (
                <Chip size="small" label={company.subscription_plan}
                  sx={{
                    height: 22, fontSize: 11, fontWeight: 700,
                    bgcolor: alpha(GOLD, 0.15), color: GOLD_SOFT,
                    border: `1px solid ${alpha(GOLD, 0.35)}`,
                  }} />
              )}
              {company.isDefault && (
                <Chip size="small" label="DEFAULT"
                  sx={{
                    height: 22, fontSize: 11, fontWeight: 700,
                    bgcolor: "rgba(16,185,129,0.18)", color: "#a7f3d0",
                    border: "1px solid rgba(16,185,129,0.35)",
                  }} />
              )}
            </Box>
            <Box sx={{ display: "flex", gap: 2, mt: 0.5 }}>
              <Typography fontSize={11.5} color="rgba(255,255,255,0.6)">
                {devices.length} device{devices.length === 1 ? "" : "s"}
                {company.max_devices ? ` / ${company.max_devices} cap` : " · no cap"}
              </Typography>
              {company.companyEmail && (
                <Typography fontSize={11.5} color="rgba(255,255,255,0.6)">
                  {company.companyEmail}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>

        <Tabs value={tab} onChange={(_, v) => setTab(v)}
          textColor="inherit"
          TabIndicatorProps={{ sx: { bgcolor: GOLD, height: 3 } }}
          sx={{
            "& .MuiTab-root": {
              color: "rgba(255,255,255,0.6)", textTransform: "none", fontWeight: 600,
              fontSize: 13, minHeight: 48, py: 1.5,
            },
            "& .Mui-selected": { color: "#fff !important" },
          }}>
          <Tab icon={<BusinessIcon sx={{ fontSize: 16 }} />} iconPosition="start" label="Profile" />
          <Tab icon={<GroupIcon sx={{ fontSize: 16 }} />} iconPosition="start" label="Users" />
          <Tab icon={<DevicesIcon sx={{ fontSize: 16 }} />} iconPosition="start" label="Devices" />
          <Tab icon={<PlaylistPlayIcon sx={{ fontSize: 16 }} />} iconPosition="start" label="Content" />
          <Tab icon={<PaidIcon sx={{ fontSize: 16 }} />} iconPosition="start" label="Billing" />
          <Tab icon={<HistoryIcon sx={{ fontSize: 16 }} />} iconPosition="start" label="Activity" />
        </Tabs>
      </Box>

      <Box sx={{ p: 3 }}>
        <TabPanel value={tab} index={0}>
          {/* Reuse the existing Edit form. It reads the company id from
              the URL, so we render it scoped to this route's id. */}
          <Box sx={{ mx: -3, mt: -3 }}>
            <CompanyEdit />
          </Box>
        </TabPanel>

        <TabPanel value={tab} index={1}>
          <SignageUsersList companyIdFilter={companyId} embedded />
        </TabPanel>

        <TabPanel value={tab} index={2}>
          <DeepLinkCard
            icon={<DevicesIcon />}
            title={`${devices.length} device${devices.length === 1 ? "" : "s"} assigned`}
            subtitle={`${activeDevices.length} active · ${devices.length - activeDevices.length} disabled`}
            cta="Open device list"
            onOpen={() => navigate(`/device/list?company_id=${companyId}`)}
          />
        </TabPanel>

        <TabPanel value={tab} index={3}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <DeepLinkCard
                icon={<PlaylistPlayIcon />}
                title="Playlists"
                subtitle="Manage the slideshows that play on this company's devices."
                cta="Open playlists"
                onOpen={() => navigate(`/playlist/list?company_id=${companyId}`)}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <DeepLinkCard
                icon={<CollectionsBookmarkIcon />}
                title="Playlist Groups"
                subtitle="Chain multiple playlists for scheduled rotation."
                cta="Open groups"
                onOpen={() => navigate(`/playlist-groups/list?company_id=${companyId}`)}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <DeepLinkCard
                icon={<PermMediaIcon />}
                title="Media Library"
                subtitle="Images and videos uploaded under this company."
                cta="Open library"
                onOpen={() => navigate(`/media/library?company_id=${companyId}`)}
              />
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={tab} index={4}>
          <BillingSummary
            company={company}
            deviceCount={devices.length}
            activeCount={activeDevices.length}
            monthlyBilling={monthlyBilling}
            isSuper={isSuper}
          />
        </TabPanel>

        <TabPanel value={tab} index={5}>
          <SignageActivityList companyIdFilter={companyId} embedded />
        </TabPanel>
      </Box>
    </Box>
  );
};

const DeepLinkCard: React.FC<{
  icon: React.ReactElement;
  title: string;
  subtitle: string;
  cta: string;
  onOpen: () => void;
}> = ({ icon, title, subtitle, cta, onOpen }) => (
  <Paper elevation={0} sx={{
    p: 3, borderRadius: 3, border: `1px solid ${BORDER}`, bgcolor: "#fff",
    display: "flex", alignItems: "center", gap: 2.5,
  }}>
    <Box sx={{
      width: 56, height: 56, borderRadius: 2,
      display: "flex", alignItems: "center", justifyContent: "center",
      background: `linear-gradient(135deg, ${alpha("#7c3aed", 0.18)} 0%, ${alpha("#7c3aed", 0.06)} 100%)`,
      color: "#7c3aed",
    }}>
      {React.cloneElement(icon, { sx: { fontSize: 28 } })}
    </Box>
    <Box sx={{ flex: 1 }}>
      <Typography sx={{ fontSize: 15, fontWeight: 700, color: NAVY, mb: 0.5 }}>
        {title}
      </Typography>
      <Typography sx={{ fontSize: 12, color: TEXT_SECONDARY }}>
        {subtitle}
      </Typography>
    </Box>
    <Button variant="outlined" size="small"
      endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
      onClick={onOpen}
      sx={{
        borderRadius: 2, textTransform: "none", fontSize: 13, fontWeight: 600,
        borderColor: BORDER, color: NAVY,
        "&:hover": { borderColor: "#7c3aed", color: "#7c3aed", bgcolor: alpha("#7c3aed", 0.04) },
      }}>
      {cta}
    </Button>
  </Paper>
);

const BillingMetric: React.FC<{ label: string; value: React.ReactNode; tint?: string }> = ({ label, value, tint = NAVY }) => (
  <Paper elevation={0} sx={{
    p: 3, borderRadius: 3, border: `1px solid ${BORDER}`, bgcolor: "#fff",
  }}>
    <Typography sx={{
      fontSize: 11, fontWeight: 700, color: TEXT_SECONDARY,
      textTransform: "uppercase", letterSpacing: "0.06em", mb: 1,
    }}>
      {label}
    </Typography>
    <Typography sx={{ fontSize: 28, fontWeight: 800, color: tint, lineHeight: 1.1 }}>
      {value}
    </Typography>
  </Paper>
);

const SUB_STATUS_STYLE: Record<string, { bg: string; fg: string }> = {
  trial:     { bg: "#dbeafe", fg: "#1e40af" },
  active:    { bg: "#dcfce7", fg: "#166534" },
  past_due:  { bg: "#fef3c7", fg: "#92400e" },
  suspended: { bg: "#fee2e2", fg: "#991b1b" },
  cancelled: { bg: "#f1f5f9", fg: "#475569" },
};

const BillingSummary: React.FC<{
  company: any;
  deviceCount: number;
  activeCount: number;
  monthlyBilling: number;
  isSuper: boolean;
}> = ({ company, deviceCount, activeCount, monthlyBilling, isSuper }) => {
  const { showMessage, showErrorMessage } = useNotifier();
  const [updatePlan, { isLoading: isSaving }] = useUpdateCompanyPlanMutation();
  const [extendTrial, { isLoading: isExtending }] = useExtendTrialMutation();
  const [suspendCo, { isLoading: isSuspending }] = useSuspendCompanyMutation();
  const [reactivateCo, { isLoading: isReactivating }] = useReactivateCompanyMutation();
  const [genPin, { isLoading: isGenPin }] = useGenerateInstallerPinMutation();
  const [pinInfo, setPinInfo] = useState<{ pin: string; url: string; expires_at: string } | null>(null);

  const [planOpen, setPlanOpen] = useState(false);
  const [plan, setPlan] = useState(company.subscription_plan || "");
  const [maxDevices, setMaxDevices] = useState<string>(
    company.max_devices != null ? String(company.max_devices) : "",
  );
  const [billingEmail, setBillingEmail] = useState(company.billing_email || "");

  const [extendOpen, setExtendOpen] = useState(false);
  const [extendDays, setExtendDays] = useState("14");

  const [suspendOpen, setSuspendOpen] = useState(false);
  const [suspendReason, setSuspendReason] = useState("");

  const subStatus: string = company.subscription_status || "trial";
  const subStyle = SUB_STATUS_STYLE[subStatus] || SUB_STATUS_STYLE.cancelled;
  const isSuspended = subStatus === "suspended" || subStatus === "cancelled";

  const trialEndsAt = company.trial_ends_at
    ? new Date(company.trial_ends_at).toLocaleDateString() : null;
  const trialDaysLeft: number | null = company.trial_days_left ?? null;

  const savePlan = async () => {
    try {
      const res = await updatePlan({
        id: company.id,
        subscription_plan: plan || null,
        max_devices: maxDevices === "" ? null : Number(maxDevices),
        billing_email: billingEmail || null,
      }).unwrap();
      if (res.status) { showMessage("Plan updated"); setPlanOpen(false); }
    } catch (e: any) { showErrorMessage(e?.data?.message || "Save failed"); }
  };

  const doExtend = async () => {
    try {
      const days = Math.max(1, parseInt(extendDays || "0", 10));
      const res = await extendTrial({ id: company.id, days }).unwrap();
      if (res.status) { showMessage(`Trial extended ${days}d`); setExtendOpen(false); }
    } catch (e: any) { showErrorMessage(e?.data?.message || "Failed"); }
  };

  const doSuspend = async () => {
    try {
      const res = await suspendCo({ id: company.id, reason: suspendReason || undefined }).unwrap();
      if (res.status) {
        showMessage("Company suspended");
        setSuspendOpen(false); setSuspendReason("");
      }
    } catch (e: any) { showErrorMessage(e?.data?.message || "Failed"); }
  };

  const doReactivate = async () => {
    try {
      const res = await reactivateCo({ id: company.id, status: "active" }).unwrap();
      if (res.status) { showMessage("Company reactivated"); }
    } catch (e: any) { showErrorMessage(e?.data?.message || "Failed"); }
  };

  const doGenPin = async () => {
    try {
      const res = await genPin(company.id).unwrap();
      if (res.status) {
        setPinInfo((res.data as any));
        showMessage("Installer PIN generated");
      }
    } catch (e: any) { showErrorMessage(e?.data?.message || "Failed"); }
  };

  return (
    <Box>
      <Typography sx={{ fontSize: 13, color: TEXT_SECONDARY, mb: 2 }}>
        Billing is driven by the active device count and per-device monthly rates.
        Payment processing is not yet enabled — these numbers preview what will be invoiced.
      </Typography>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <BillingMetric label="Plan" value={company.subscription_plan || "—"} />
        </Grid>
        <Grid item xs={12} md={3}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: `1px solid ${BORDER}`, bgcolor: "#fff" }}>
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: TEXT_SECONDARY,
              textTransform: "uppercase", letterSpacing: "0.06em", mb: 1 }}>
              Status
            </Typography>
            <Chip size="small" label={subStatus.toUpperCase()}
              sx={{ height: 28, fontSize: 13, fontWeight: 800,
                bgcolor: subStyle.bg, color: subStyle.fg, mt: 0.5 }} />
          </Paper>
        </Grid>
        <Grid item xs={12} md={3}>
          <BillingMetric
            label="Trial Ends"
            value={trialEndsAt
              ? <span>{trialEndsAt}{trialDaysLeft !== null && (
                  <Typography component="span" sx={{
                    ml: 1, fontSize: 14,
                    color: trialDaysLeft < 0 ? "#b91c1c"
                      : trialDaysLeft < 3 ? "#f59e0b" : TEXT_SECONDARY,
                  }}>
                    ({trialDaysLeft}d)
                  </Typography>)}
                </span>
              : "—"}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <BillingMetric label="Est. monthly" value={formatMoney(monthlyBilling)} tint={GOLD_SOFT} />
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <BillingMetric label="Active devices" value={activeCount} tint="#10b981" />
        </Grid>
        <Grid item xs={12} md={3}>
          <BillingMetric label="Total devices" value={deviceCount} />
        </Grid>
        <Grid item xs={12} md={3}>
          <BillingMetric label="Device cap"
            value={company.max_devices ?? "—"}
            tint={company.max_devices && deviceCount >= company.max_devices ? "#dc2626" : NAVY} />
        </Grid>
        <Grid item xs={12} md={3}>
          <BillingMetric
            label="Avg. price/device"
            value={activeCount > 0 ? formatMoney(monthlyBilling / activeCount) : "—"} />
        </Grid>
      </Grid>

      {/* Super-admin actions */}
      {isSuper && (
        <Paper elevation={0} sx={{
          p: 3, borderRadius: 3, border: `1px solid ${BORDER}`, bgcolor: "#fff", mb: 3,
        }}>
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: NAVY, mb: 2 }}>
            Super-admin controls
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
            <Button variant="outlined" size="small" onClick={() => setPlanOpen(true)}
              sx={{ textTransform: "none", borderRadius: 2, fontWeight: 600 }}>
              Change Plan / Cap
            </Button>
            <Button variant="outlined" size="small" onClick={() => setExtendOpen(true)}
              sx={{ textTransform: "none", borderRadius: 2, fontWeight: 600 }}>
              Extend Trial
            </Button>
            <Button variant="outlined" size="small" onClick={doGenPin} disabled={isGenPin}
              sx={{ textTransform: "none", borderRadius: 2, fontWeight: 600 }}>
              Generate Installer PIN
            </Button>
            {isSuspended ? (
              <Button variant="contained" size="small" disableElevation
                onClick={doReactivate} disabled={isReactivating}
                sx={{
                  textTransform: "none", borderRadius: 2, fontWeight: 700,
                  bgcolor: "#10b981", "&:hover": { bgcolor: "#059669" },
                }}>
                Reactivate
              </Button>
            ) : (
              <Button variant="contained" size="small" disableElevation color="error"
                onClick={() => setSuspendOpen(true)} disabled={isSuspending}
                sx={{ textTransform: "none", borderRadius: 2, fontWeight: 700 }}>
                Suspend
              </Button>
            )}
          </Box>
          {company.billing_email && (
            <Typography sx={{ fontSize: 12, color: TEXT_SECONDARY, mt: 2 }}>
              Billing email: <strong>{company.billing_email}</strong>
            </Typography>
          )}
        </Paper>
      )}

      {/* Change Plan dialog */}
      <Dialog open={planOpen} onClose={() => setPlanOpen(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontSize: 16, fontWeight: 800, color: NAVY }}>Change Plan</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "grid", gap: 2, mt: 1 }}>
            <TextField select size="small" label="Subscription plan" value={plan}
              onChange={(e) => setPlan(e.target.value)}>
              <MenuItem value="">— None —</MenuItem>
              <MenuItem value="starter">Starter</MenuItem>
              <MenuItem value="business">Business</MenuItem>
              <MenuItem value="enterprise">Enterprise</MenuItem>
            </TextField>
            <TextField size="small" type="number" label="Device cap (blank = unlimited)"
              value={maxDevices} onChange={(e) => setMaxDevices(e.target.value)} />
            <TextField size="small" type="email" label="Billing email"
              value={billingEmail} onChange={(e) => setBillingEmail(e.target.value)} />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setPlanOpen(false)} sx={{ textTransform: "none", fontWeight: 600 }}>
            Cancel
          </Button>
          <Button variant="contained" disableElevation onClick={savePlan} disabled={isSaving}
            sx={{ textTransform: "none", fontWeight: 700, bgcolor: GOLD, color: "#0b1e2d" }}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Extend Trial dialog */}
      <Dialog open={extendOpen} onClose={() => setExtendOpen(false)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontSize: 16, fontWeight: 800, color: NAVY }}>Extend Trial</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 13, color: TEXT_SECONDARY, mb: 2 }}>
            Add days to the trial. If the trial has already expired, the new period starts today.
          </Typography>
          <TextField fullWidth size="small" type="number" label="Days"
            value={extendDays} onChange={(e) => setExtendDays(e.target.value)} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setExtendOpen(false)} sx={{ textTransform: "none", fontWeight: 600 }}>
            Cancel
          </Button>
          <Button variant="contained" disableElevation onClick={doExtend} disabled={isExtending}
            sx={{ textTransform: "none", fontWeight: 700, bgcolor: GOLD, color: "#0b1e2d" }}>
            Extend
          </Button>
        </DialogActions>
      </Dialog>

      {/* Installer PIN dialog */}
      <Dialog open={!!pinInfo} onClose={() => setPinInfo(null)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontSize: 16, fontWeight: 800, color: NAVY }}>
          Installer PIN Generated
        </DialogTitle>
        <DialogContent>
          {pinInfo && (
            <Box>
              <Typography sx={{ fontSize: 13, color: TEXT_SECONDARY, mb: 2 }}>
                Share this PIN with the on-site installer. They can enter it at
                <code style={{ background: "#f1f5f9", padding: "2px 6px", borderRadius: 4, margin: "0 4px" }}>
                  /installer
                </code>
                to see device codes and the APK download — no admin login needed.
              </Typography>
              <Box sx={{
                p: 3, textAlign: "center", borderRadius: 2,
                background: `linear-gradient(135deg, ${alpha("#7c3aed", 0.08)} 0%, ${alpha("#7c3aed", 0.02)} 100%)`,
                border: `1px solid ${alpha("#7c3aed", 0.3)}`,
              }}>
                <Typography sx={{
                  fontSize: 38, fontWeight: 800, letterSpacing: "0.15em",
                  fontFamily: "monospace", color: "#7c3aed",
                }}>
                  {pinInfo.pin}
                </Typography>
                <Typography sx={{ fontSize: 12, color: TEXT_SECONDARY, mt: 1 }}>
                  Expires {new Date(pinInfo.expires_at).toLocaleString()}
                </Typography>
              </Box>
              <Typography sx={{ fontSize: 12, color: TEXT_SECONDARY, mt: 2, fontFamily: "monospace",
                wordBreak: "break-all" }}>
                {pinInfo.url}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => navigator.clipboard.writeText(pinInfo?.url ?? "")}
            sx={{ textTransform: "none" }}>
            Copy URL
          </Button>
          <Button variant="contained" disableElevation onClick={() => setPinInfo(null)}
            sx={{ textTransform: "none", fontWeight: 700 }}>
            Done
          </Button>
        </DialogActions>
      </Dialog>

      {/* Suspend dialog */}
      <Dialog open={suspendOpen} onClose={() => setSuspendOpen(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontSize: 16, fontWeight: 800, color: NAVY }}>Suspend Company</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 13, color: TEXT_SECONDARY, mb: 2 }}>
            Suspending blocks the Player API for every device in this company immediately. Players show "Device Disabled" until reactivated.
          </Typography>
          <TextField fullWidth size="small" label="Reason (optional)"
            multiline rows={2}
            value={suspendReason} onChange={(e) => setSuspendReason(e.target.value)} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setSuspendOpen(false)} sx={{ textTransform: "none", fontWeight: 600 }}>
            Cancel
          </Button>
          <Button variant="contained" disableElevation color="error" onClick={doSuspend} disabled={isSuspending}
            sx={{ textTransform: "none", fontWeight: 700 }}>
            Suspend
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const FieldRow: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Box>
    <Typography sx={{
      fontSize: 10.5, fontWeight: 700, color: TEXT_SECONDARY,
      textTransform: "uppercase", letterSpacing: "0.06em", mb: 0.5,
    }}>
      {label}
    </Typography>
    <Typography sx={{ fontSize: 14, fontWeight: 700, color: TEXT_PRIMARY }}>
      {value}
    </Typography>
  </Box>
);

export default CompanyDetail;
